import {usePowerSync} from '@powersync/react';
import {useCallback} from 'react';
import {
  CreateListDataDTO,
  CreateTaskTypeListDataDTO,
  UpdateListDataDTO,
  UpdateTaskTypeListDataDTO,
} from '../Types/DtoTypes';
import {
  Document as DocumentModel,
  EquipmentHeavyProfile,
  EquipmentOtherProfile,
  EquipmentVehicleProfile,
  Organisation,
  Person,
  Project,
  SQLUser,
  Submission,
  Submission_answer,
  Submission_submissionStatus,
  SubmissionBundle,
  SubmissionLink,
  SubmissionRelation,
  SubmissionTraining,
} from '../Models/PowerSyncModels/models';
import {usePowerSyncData} from '../Providers/PowerSyncDataProvider';
import {ControlTypes} from '../Constants/ControlTypes';
import {DynamicAttachment} from '../Types/ControlTypes';
import AttachmentService from '../Services/AttachmentService';
import {IAttachmentService} from '../Services/Interfaces/IAttachmentService';

const usePowerSyncWriteData = () => {
  const db = usePowerSync();
  const {submissions} = usePowerSyncData();

  const deleteItems = useCallback(
    async (ids: string[], tableName: string) => {
      try {
        // start a write transaction to insert the new Item
        let idFill = Array(ids.length).fill('?').join(', ');
        db.writeTransaction(async tx => {
          await tx.execute(
            `DELETE FROM ` + tableName + ` WHERE _id IN (` + idFill + `) `,
            ids,
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const createDocument = useCallback(
    async (doc: DocumentModel) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            'INSERT INTO Documents (id, SQLServerId, name, partition, documentTypeId, showOffline, showCompanyWide, attachmentJSON, createdBy) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)',
            [
              doc._id,
              doc.SQLServerId,
              doc.name,
              doc.partition,
              doc.documentTypeId,
              doc.showOffline,
              doc.showCompanyWide,
              doc.attachmentJSON,
              doc.createdBy,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateDocument = useCallback(
    async (doc: DocumentModel) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE Documents
             SET
               name = ?,
               documentTypeId = ?,
               showOffline = ?,
               showCompanyWide = ?,
               attachmentJSON = ? WHERE id = ?`,
            [
              doc.name,
              doc.documentTypeId,
              doc.showOffline,
              doc.showCompanyWide,
              doc.attachmentJSON,
              doc._id,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const createHeavyEquipment = useCallback(
    async (heavyEquip: EquipmentHeavyProfile) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `INSERT INTO EquipmentHeavyProfiles (id, name, number, description, hoursUsed, ownershipType, status, photo, photos, partition, dataHubVersion, SQLServerId, createDateTimeStamp) 
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              heavyEquip._id,
              heavyEquip.name,
              heavyEquip.number,
              heavyEquip.description,
              heavyEquip.hoursUsed,
              heavyEquip.ownershipType,
              heavyEquip.status,
              heavyEquip.photo,
              heavyEquip.photos,
              heavyEquip.partition,
              Math.floor(Date.now() / 1000),
              heavyEquip.SQLServerId,
              new Date().toISOString(),
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateHeavyEquipment = useCallback(
    async (heavyEquip: EquipmentHeavyProfile) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE EquipmentHeavyProfiles
             SET
              name = ?,
              number = ?,
              description = ?,
              hoursUsed = ?,
              ownershipType = ?,
              status = ?,
              photo = ?,
              photos = ?,
              dataHubVersion = ?
              WHERE id = ?`,
            [
              heavyEquip.name,
              heavyEquip.number,
              heavyEquip.description,
              heavyEquip.hoursUsed,
              heavyEquip.ownershipType,
              heavyEquip.status,
              heavyEquip.photo,
              heavyEquip.photos,
              Math.floor(Date.now() / 1000),
              heavyEquip._id,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const createOtherEquipment = useCallback(
    async (otherEquip: EquipmentOtherProfile) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `INSERT INTO EquipmentOtherProfiles (id, name, number, description, ownershipType, status, photo, photos, partition, dataHubVersion, SQLServerId, createDateTimeStamp) 
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              otherEquip._id,
              otherEquip.name,
              otherEquip.number,
              otherEquip.description,
              otherEquip.ownershipType,
              otherEquip.status,
              otherEquip.photo,
              otherEquip.photos,
              otherEquip.partition,
              Math.floor(Date.now() / 1000),
              otherEquip.SQLServerId,
              new Date().toISOString(),
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateOtherEquipment = useCallback(
    async (otherEquip: EquipmentOtherProfile) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE EquipmentOtherProfiles
             SET
              name = ?,
              number = ?,
              description = ?,
              ownershipType = ?,
              status = ?,
              photo = ?,
              photos = ?,
              dataHubVersion = ?
              WHERE id = ?`,
            [
              otherEquip.name,
              otherEquip.number,
              otherEquip.description,
              otherEquip.ownershipType,
              otherEquip.status,
              otherEquip.photo,
              otherEquip.photos,
              Math.floor(Date.now() / 1000),
              otherEquip._id,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const createVehicleEquipment = useCallback(
    async (vehicleEquip: EquipmentVehicleProfile) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `INSERT INTO EquipmentVehicleProfiles (id, name, number, description, licensePlate, distance, ownershipType, status, photo, photos, partition, dataHubVersion, SQLServerId, createDateTimeStamp) 
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              vehicleEquip._id,
              vehicleEquip.name,
              vehicleEquip.number,
              vehicleEquip.description,
              vehicleEquip.licensePlate,
              vehicleEquip.distance,
              vehicleEquip.ownershipType,
              vehicleEquip.status,
              vehicleEquip.photo,
              vehicleEquip.photos,
              vehicleEquip.partition,
              Math.floor(Date.now() / 1000),
              vehicleEquip.SQLServerId,
              new Date().toISOString(),
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateVehicleEquipment = useCallback(
    async (vehicleEquip: EquipmentVehicleProfile) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE EquipmentVehicleProfiles
             SET
              name = ?,
              number = ?,
              description = ?,
              licensePlate = ?,
              distance = ?,
              ownershipType = ?,
              status = ?,
              photo = ?,
              photos = ?,
              dataHubVersion = ?
              WHERE id = ?`,
            [
              vehicleEquip.name,
              vehicleEquip.number,
              vehicleEquip.description,
              vehicleEquip.licensePlate,
              vehicleEquip.distance,
              vehicleEquip.ownershipType,
              vehicleEquip.status,
              vehicleEquip.photo,
              vehicleEquip.photos,
              Math.floor(Date.now() / 1000),
              vehicleEquip._id,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const createListData = useCallback(
    async (tableName: string, createDto: CreateListDataDTO) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            'INSERT INTO ' +
              tableName +
              ' (id, _id, name, sortOrder, partition, isActive, isDeleted, SQLServerId, dataHubVersion, createDateTimeStamp) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)',
            [
              createDto.id,
              createDto.id,
              createDto.name,
              createDto.sortOrder,
              createDto.partition,
              createDto.isActive,
              false,
              createDto.SQLServerId,
              Math.floor(Date.now() / 1000),
              new Date().toISOString(),
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateListData = useCallback(
    async (tableName: string, updateDto: UpdateListDataDTO) => {
      try {
        // start a write transaction to insert the new Item
        let res = await db.execute(
          `UPDATE ` +
            tableName +
            ` SET
               name = ?,
               sortOrder = ?,
               isActive = ?,
               dataHubVersion = ? WHERE id = ?`,
          [
            updateDto.name,
            updateDto.sortOrder,
            updateDto.isActive,
            Math.floor(Date.now() / 1000),
            updateDto.id,
          ],
        );
      } catch (ex: any) {
        console.log(ex);
      }
    },
    [db],
  );

  const createListDataTask = useCallback(
    async (createDto: CreateTaskTypeListDataDTO) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            'INSERT INTO Tasks (id, name, taskTypeSQLServerId, sortOrder, partition, isActive, isDeleted, SQLServerId, dataHubVersion, createDateTimeStamp) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)',
            [
              createDto.id,
              createDto.name,
              createDto.taskTypeSQLServerId,
              createDto.sortOrder,
              createDto.partition,
              createDto.isActive,
              false,
              createDto.SQLServerId,
              Math.floor(Date.now() / 1000),
              new Date().toISOString(),
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateListDataTask = useCallback(
    async (updateDto: UpdateTaskTypeListDataDTO) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE Tasks SET
               name = ?,
               taskTypeSQLServerId = ?,
               sortOrder = ?,
               isActive = ?,
               dataHubVersion = ? WHERE id = ?`,
            [
              updateDto.name,
              updateDto.taskTypeSQLServerId,
              updateDto.sortOrder,
              updateDto.isActive,
              Math.floor(Date.now() / 1000),
              updateDto.id,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateOrganisation = useCallback(
    async (org: Organisation) => {
      try {
        // start a write transaction to insert the new Item
        await db.execute(
          `UPDATE Organisations SET
                dataHubVersion = ?,
                name = ?,
                CORNumber = ?,
                SAFENumber = ?,
                WSBCNumber = ?,
                WSBCUnitsJSON = ?,
                mainActivities = ?,
                headOfficeLocationJSON = ?,
                mailingLocationJSON = ?,
                primaryContactJSON = ?,
                auditContactJSON = ?,
                safetyContactJSON = ?,
                ownersJSON = ?,
                tradeName = ?
                WHERE id = ?`,
          [
            org.dataHubVersion,
            org.name,
            org.CORNumber,
            org.SAFENumber,
            org.WSBCNumber,
            org.WSBCUnitsJSON,
            org.mainActivities,
            org.headOfficeLocationJSON,
            org.mailingLocationJSON,
            org.primaryContactJSON,
            org.auditContactJSON,
            org.safetyContactJSON,
            org.ownersJSON,
            org.tradeName,
            org._id,
          ],
        );
      } catch (ex: any) {
        console.log(ex);
      }
    },
    [db],
  );

  const createPerson = useCallback(
    async (person: Person) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `INSERT INTO People (id,
                                      _id,
                                      firstName,
                                      lastName,
                                      email,
                                      phone,
                                      fax,
                                      phoneDirect,
                                      supervisorId,
                                      supervisorSQLServerId,
                                      partition,
                                      isActive,
                                      isDeleted,
                                      SQLServerId,
                                      datahubVersion,
                                      jobId,
                                      jobSQLServerId,
                                      peopleTypeId,
                                      peopleTypeSQLServerId,
                                      photo,
                                      photoThumbnail,
                                      hireDate,
                                      inactiveDate,
                                      employeeNumber,
                                      createDateTimeStamp)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              person._id,
              person._id,
              person.firstName,
              person.lastName,
              person.email,
              person.phone,
              person.fax,
              person.phoneDirect,
              person.supervisorId,
              person.supervisorSQLServerId,
              person.partition,
              person.isActive,
              person.isDeleted,
              person.SQLServerId,
              Math.floor(Date.now() / 1000),
              person.jobId,
              person.jobSQLServerId,
              person.peopleTypeId,
              person.peopleTypeSQLServerId,
              person.photo,
              person.photoThumbnail,
              person.hireDate ? person.hireDate?.toISOString() : null,
              person.inactiveDate ? person.inactiveDate.toISOString() : null,
              person.employeeNumber,
              new Date().toISOString(),
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updatePerson = useCallback(
    async (person: Person) => {
      try {
        await db.execute(
          `UPDATE People SET
              firstName = ?,
              lastName = ?,
              email = ?,
              phone = ?,
              fax = ?,
              phoneDirect = ?,
              supervisorId = ?,
              supervisorSQLServerId = ?,
              isActive = ?,
              isDeleted = ?,
              SQLServerId = ?,
              jobId = ?,
              jobSQLServerId = ?,
              peopleTypeId = ?,
              peopleTypeSQLServerId = ?,
              photo = ?,
              photoThumbnail = ?,
              hireDate = ?,
              inactiveDate = ?,
              employeeNumber = ?,
              dataHubVersion = ?
              WHERE id = ?`,
          [
            person.firstName,
            person.lastName,
            person.email,
            person.phone,
            person.fax,
            person.phoneDirect,
            person.supervisorId,
            person.supervisorSQLServerId,
            person.isActive,
            person.isDeleted,
            person.SQLServerId,
            person.jobId,
            person.jobSQLServerId,
            person.peopleTypeId,
            person.peopleTypeSQLServerId,
            person.photo,
            person.photoThumbnail,
            person.hireDate ? person.hireDate?.toISOString() : null,
            person.inactiveDate ? person.inactiveDate.toISOString() : null,
            person.employeeNumber,
            Math.floor(Date.now() / 1000),
            person._id,
          ],
        );
      } catch (ex: any) {
        console.log(ex);
      }
    },
    [db],
  );

  const createProject = useCallback(
    async (project: Project) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `INSERT INTO Projects (id,
                                  name,
                                  projectManagerId,
                                  projectManagerSQLServerId,
                                  partition,
                                  isActive,
                                  isDeleted,
                                  location,
                                  activeStartDate,
                                  activeEndDate,
                                  SQLServerId,
                                  safetyResourceId,
                                  safetyResourceSQLServerId,
                                  siteSupervisorId,
                                  siteSupervisorSQLServerId,
                                  workers,
                                  workersJSON,
                                  address,
                                  cityName,
                                  citySQLServerId,
                                  countrySQLServerId,
                                  postalZip,
                                  provStateSQLServerId,
                                  dataHubVersion,
                                  createDateTimeStamp)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              project._id,
              project.name,
              project.projectManagerId,
              project.projectManagerSQLServerId,
              project.partition,
              project.isActive,
              project.isDeleted,
              JSON.stringify(project.location),
              project.activeStartDate
                ? project.activeStartDate.toISOString()
                : null,
              project.activeEndDate
                ? project.activeEndDate.toISOString()
                : null,
              project.SQLServerId,
              project.safetyResourceId,
              project.safetyResourceSQLServerId,
              project.siteSupervisorId,
              project.siteSupervisorSQLServerId,
              JSON.stringify(project.workers),
              project.workersJSON,
              project.address,
              project.cityName,
              project.citySQLServerId,
              project.countrySQLServerId,
              project.postalZip,
              project.provStateSQLServerId,
              Math.floor(Date.now() / 1000),
              new Date().toISOString(),
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateProject = useCallback(
    async (project: Project) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE Projects SET
                name = ?,
                projectManagerId = ?,
                projectManagerSQLServerId = ?,
                isActive = ?,
                isDeleted = ?,
                location = ?,
                activeStartDate = ?,
                activeEndDate = ?,
                safetyResourceId = ?,
                safetyResourceSQLServerId = ?,
                siteSupervisorId = ?,
                siteSupervisorSQLServerId = ?,
                workers = ?,
                workersJSON = ?,
                address = ?,
                cityName = ?,
                citySQLServerId = ?,
                countrySQLServerId = ?,
                postalZip = ?,
                provStateSQLServerId = ?,
                dataHubVersion = ?
              WHERE id = ?`,
            [
              project.name,
              project.projectManagerId,
              project.projectManagerSQLServerId,
              project.isActive,
              project.isDeleted,
              JSON.stringify(project.location),
              project.activeStartDate
                ? project.activeStartDate.toISOString()
                : null,
              project.activeEndDate
                ? project.activeEndDate.toISOString()
                : null,
              project.safetyResourceId,
              project.safetyResourceSQLServerId,
              project.siteSupervisorId,
              project.siteSupervisorSQLServerId,
              JSON.stringify(project.workers),
              project.workersJSON,
              project.address,
              project.cityName,
              project.citySQLServerId,
              project.countrySQLServerId,
              project.postalZip,
              project.provStateSQLServerId,
              Math.floor(Date.now() / 1000),
              project._id,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const createSQLUser = useCallback(
    async (user: SQLUser) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `INSERT INTO SQLUsers (id,
                                      firstName,
                                      lastName,
                                      partition,
                                      email,
                                      isActive,
                                      isDeleted,
                                      SQLServerId,
                                      SQLServerPersonId,
                                      SQLServerInvitedByPersonId,
                                      statusObj,
                                      SQLServerRoleIds,
                                      dataHubVersion,
                                      createDateTimeStamp)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              user._id,
              user.firstName,
              user.lastName,
              user.partition,
              user.email,
              user.isActive,
              user.isDeleted,
              user.SQLServerId,
              user.SQLServerPersonId,
              user.SQLServerInvitedByPersonId,
              JSON.stringify(user.statusObj),
              JSON.stringify(user.SQLServerRoleIds),
              Math.floor(Date.now() / 1000),
              new Date().toISOString(),
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateSQLUser = useCallback(
    async (user: SQLUser) => {
      try {
        await db.execute(
          `UPDATE SQLUsers SET
               firstName = ?,
                lastName = ?,
                email = ?,
                isActive = ?,
                statusObj = ?,
                SQLServerRoleIds = ?,
                dataHubVersion = ?
              WHERE _id = ?`,
          [
            user.firstName,
            user.lastName,
            user.email,
            user.isActive,
            JSON.stringify(user.statusObj),
            JSON.stringify(user.SQLServerRoleIds),
            Math.floor(Date.now() / 1000),
            user._id,
          ],
        );
      } catch (ex: any) {
        console.log(ex);
      }
    },
    [db],
  );

  const createSubmission = async (submission: Submission) => {
    try {
      // start a write transaction to insert the new Item
      db.writeTransaction(async tx => {
        await tx.execute(
          `INSERT INTO Submissions (id,
          _id,
                                      partition,
                                      createdBy,
                                      createDateTimeStamp,
                                      updatedBy,
                                      updateDateTimeStamp,
                                      SQLServerId,
                                      templateVersion,
                                      templateType,
                                      metadataJSON,
                                      answers,
                                      submissionStatus,
                                      dataHubVersion,
                                      attachmentsUploaded,
                                      submissionStatusLock,
                                      isArchived)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            submission._id,
            submission._id,
            submission.partition,
            submission.createdBy,
            new Date().toISOString(),
            submission.updatedBy,
            new Date().toISOString(),
            submission.SQLServerId,
            JSON.stringify(submission.templateVersion),
            JSON.stringify(submission.templateType),
            submission.metadataJSON,
            JSON.stringify(submission.answers),
            JSON.stringify(submission.submissionStatus),
            Math.floor(Date.now() / 1000),
            submission.attachmentsUploaded,
            submission.submissionStatusLock,
            false,
          ],
        );
      });
    } catch (ex: any) {}
  };

  const updateSubmission = useCallback(
    async (submission: Submission) => {
      try {
        // start a write transaction to insert the new Item

        await db.execute(
          `UPDATE Submissions
                SET
                submittedBy = ?,
                submittedDateTimeStamp = ?,
                updatedBy = ?,
                updateDateTimeStamp = ?,
                metadataJSON = ?,
                answers = ?,
                submissionStatus = ?,
                dataHubVersion = ?,
                attachmentsUploaded = ?,
                submissionStatusLock = ?
              WHERE _id = ?`,
          [
            submission.submittedBy,
            submission.submittedDateTimeStamp?.toISOString(),
            submission.updatedBy,
            new Date().toISOString(),
            submission.metadataJSON,
            JSON.stringify(submission.answers),
            JSON.stringify(submission.submissionStatus),
            Math.floor(Date.now() / 1000),
            submission.attachmentsUploaded,
            submission.submissionStatusLock,
            submission._id,
          ],
        );
      } catch (ex: any) {
        console.log('error updating submission');
        console.log(ex);
      }
    },
    [db],
  );

  const updateSubmissionStatus = useCallback(
    async (id: string, submissionStatus: Submission_submissionStatus) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE Submissions
                SET
                submissionStatus = ?,
                dataHubVersion = ?
              WHERE id = ?`,
            [JSON.stringify(submissionStatus), Math.floor(Date.now() / 1000)],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateSubmissionMetaData = useCallback(
    async (id: string, metaDataJSON: string) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE Submissions
                SET
                metadataJSON = ?,
                dataHubVersion = ?
              WHERE id = ?`,
            [metaDataJSON, Math.floor(Date.now() / 1000), id],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateSubmissionAnswer = useCallback(
    async (id: string, answers: Submission_answer[]) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE Submissions
                SET
                answers = ?,
                dataHubVersion = ?
              WHERE id = ?`,
            [JSON.stringify(answers), Math.floor(Date.now() / 1000), id],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const deleteSubmission = useCallback(
    async (id: string) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `DELETE FROM Submissions
              WHERE id = ?`,
            [id],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const createSubmissionTraining = useCallback(
    async (submissionTraining: SubmissionTraining) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `INSERT INTO SubmissionTrainings (id,
                                      _id,
                                      partition,
                                      createdBy,
                                      createDateTimeStamp,
                                      updatedBy,
                                      updateDateTimeStamp,
                                      SQLServerId,
                                      person,
                                      trainingType,
                                      certificateType,
                                      templateVersion,
                                      templateType,
                                      metadataJSON,
                                      answers,
                                      submissionStatus,
                                      dataHubVersion,
                                      attachmentsUploaded,
                                      submissionStatusLock)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              submissionTraining._id,
              submissionTraining._id,
              submissionTraining.partition,
              submissionTraining.createdBy,
              new Date().toISOString(),
              submissionTraining.updatedBy,
              new Date().toISOString(),
              submissionTraining.SQLServerId,
              submissionTraining.person
                ? JSON.stringify(submissionTraining.person)
                : null,
              submissionTraining.trainingType
                ? JSON.stringify(submissionTraining.trainingType)
                : null,
              submissionTraining.certificateType
                ? JSON.stringify(submissionTraining.certificateType)
                : null,
              JSON.stringify(submissionTraining.templateVersion),
              JSON.stringify(submissionTraining.templateType),
              submissionTraining.metadataJSON,
              JSON.stringify(submissionTraining.answers),
              JSON.stringify(submissionTraining.submissionStatus),
              Math.floor(Date.now() / 1000),
              submissionTraining.attachmentsUploaded,
              submissionTraining.submissionStatusLock,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const updateSubmissionTraining = useCallback(
    async (submissionTraining: SubmissionTraining) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE SubmissionTrainings SET
                submittedBy = ?,
                submittedDateTimeStamp = ?,
                updatedBy = ?,
                updateDateTimeStamp = ?,
                metadataJSON = ?,
                person = ?,
                trainingType = ?,
                certificateType = ?,
                answers = ?,
                submissionStatus = ?,
                dataHubVersion = ?,
                attachmentsUploaded = ?,
                submissionStatusLock = ?
              WHERE id = ?`,
            [
              submissionTraining.submittedBy,
              submissionTraining.submittedDateTimeStamp?.toISOString(),
              submissionTraining.updatedBy,
              new Date().toISOString(),
              submissionTraining.metadataJSON,
              JSON.stringify(submissionTraining.person),
              submissionTraining.trainingType
                ? JSON.stringify(submissionTraining.trainingType)
                : null,
              submissionTraining.certificateType
                ? JSON.stringify(submissionTraining.certificateType)
                : null,
              JSON.stringify(submissionTraining.answers),
              JSON.stringify(submissionTraining.submissionStatus),
              Math.floor(Date.now() / 1000),
              submissionTraining.attachmentsUploaded,
              JSON.stringify(submissionTraining.submissionStatusLock),
              submissionTraining._id,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const createSubmissionBundle = async (submissionBundle: SubmissionBundle) => {
    try {
      // start a write transaction to insert the new Item

      await db.execute(
        `INSERT INTO SubmissionBundles (id,
                                      _id,
                                      name,
                                      createDateTimeStamp,
                                      createdBy,
                                      externalOrgId,
                                      externalRecipients,
                                      internalRecipients,
                                      auditSubmissionId,
                                      isAudit,
                                      partition,
                                      status,
                                      submitDateTimeStamp,
                                      dataHubVersion,
                                      attachmentsUploaded,
                                      bundleAttachments,
                                      SQLServerId,
                                      submissions)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          submissionBundle._id,
          submissionBundle._id,
          submissionBundle.name,
          new Date().toISOString(),
          submissionBundle.createdBy,
          submissionBundle.externalOrgId,
          submissionBundle.externalRecipients
            ? JSON.stringify(submissionBundle.externalRecipients)
            : [],
          submissionBundle.internalRecipients
            ? JSON.stringify(submissionBundle.internalRecipients)
            : [],
          submissionBundle.auditSubmissionId,
          submissionBundle.isAudit,
          submissionBundle.partition,
          submissionBundle.status,
          submissionBundle.submitDateTimeStamp?.toISOString(),
          Math.floor(Date.now() / 1000),
          submissionBundle.attachmentsUploaded,
          submissionBundle.bundleAttachments,
          submissionBundle.SQLServerId,
          submissionBundle.submissions
            ? JSON.stringify(submissionBundle.submissions)
            : [],
        ],
      );
    } catch (ex: any) {}
  };

  const updateSubmissionBundle = useCallback(
    async (submissionBundle: SubmissionBundle) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `UPDATE SubmissionBundles SET
                name = ?,
                externalOrgId = ?,
                externalRecipients = ?,
                internalRecipients = ?,
                auditSubmissionId = ?,
                isAudit = ?,
                status = ?,
                submitDateTimeStamp = ?,
                dataHubVersion = ?,
                attachmentsUploaded = ?,
                bundleAttachments = ?,
                submissions = ?
                WHERE id = ?`,
            [
              submissionBundle.name,
              submissionBundle.externalOrgId,
              submissionBundle.externalRecipients
                ? JSON.stringify(submissionBundle.externalRecipients)
                : [],
              submissionBundle.internalRecipients
                ? JSON.stringify(submissionBundle.internalRecipients)
                : [],
              submissionBundle.auditSubmissionId,
              submissionBundle.isAudit,
              submissionBundle.status,
              submissionBundle.submitDateTimeStamp?.toISOString(),
              Math.floor(Date.now() / 1000),
              submissionBundle.attachmentsUploaded,
              submissionBundle.bundleAttachments,
              submissionBundle.submissions
                ? JSON.stringify(submissionBundle.submissions)
                : [],
              submissionBundle._id,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const createSubmissionRelation = useCallback(
    async (submissionRelation: SubmissionRelation) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `INSERT INTO SubmissionRelations (
                id,
                _id,
                createDateTimeStamp,
                partition,
                relationId,
                relationType,
                submissionId
              )
              VALUES (?, ?, ?, ?, ?, ?, ?)`,
            [
              submissionRelation._id,
              submissionRelation._id,
              new Date().toISOString(),
              submissionRelation.partition,
              submissionRelation.relationId,
              submissionRelation.relationType,
              submissionRelation.submissionId,
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const createSubmissionLink = useCallback(
    async (subLink: SubmissionLink) => {
      try {
        // start a write transaction to insert the new Item
        db.writeTransaction(async tx => {
          await tx.execute(
            `INSERT INTO SubmissionLinks (
                                      id,
                                      partition,
                                      parentSubmissionId,
                                      childSubmissionId,
                                      createDateTimeStamp)
              VALUES (?, ?, ?, ?, ?)`,
            [
              subLink._id,
              subLink.partition,
              subLink.parentSubmissionId,
              subLink.childSubmissionId,
              new Date().toISOString(),
            ],
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const deleteSubmissionLink = useCallback(
    async (ids: string[]) => {
      try {
        // start a write transaction to insert the new Item
        let idFill = Array(ids.length).fill('?').join(', ');
        db.writeTransaction(async tx => {
          await tx.execute(
            `DELETE FROM SubmissionLinks WHERE _id IN (` + idFill + `) `,
            ids,
          );
        });
      } catch (ex: any) {}
    },
    [db],
  );

  const deleteSubmissionAttachments = async (submissionId: string) => {
    let attachmentService: IAttachmentService = new AttachmentService(() => {});
    let submissionToDelete = submissions.find(x => x._id === submissionId);
    if (!submissionToDelete) return;

    let attachmentAnswers = submissionToDelete.answers.filter(
      x => x.controlTypeId == ControlTypes.PHOTOS_AND_ATTACHMENTS,
    );

    if (!attachmentAnswers || attachmentAnswers.length < 1) return;

    for (let i = 0; i < attachmentAnswers.length; i++) {
      if (!attachmentAnswers[i].answer) continue;

      let parsedAttachments = JSON.parse(
        attachmentAnswers[i].answer!,
      ) as DynamicAttachment[];

      for (let j = 0; j < parsedAttachments.length; j++) {
        await attachmentService.deleteBlobAttachment(
          parsedAttachments[j].blobUri!,
        );

        if (parsedAttachments[j].blobThumbnailUri) {
          await attachmentService.deleteBlobAttachment(
            parsedAttachments[j].blobThumbnailUri!,
          );
        }
      }
    }
  };

  return {
    deleteItems: deleteItems,
    createHeavyEquipment: createHeavyEquipment,
    updateHeavyEquipment: updateHeavyEquipment,
    createOtherEquipment: createOtherEquipment,
    updateOtherEquipment: updateOtherEquipment,
    createVehicleEquipment: createVehicleEquipment,
    updateVehicleEquipment: updateVehicleEquipment,
    createDocument: createDocument,
    updateDocument: updateDocument,
    createListData: createListData,
    updateListData: updateListData,
    createListDataTask: createListDataTask,
    updateListDataTask: updateListDataTask,
    updateOrganisation: updateOrganisation,
    createPerson: createPerson,
    updatePerson: updatePerson,
    createProject: createProject,
    updateProject: updateProject,
    createSQLUser: createSQLUser,
    updateSQLUser: updateSQLUser,
    createSubmission: createSubmission,
    updateSubmission: updateSubmission,
    deleteSubmission: deleteSubmission,
    updateSubmissionStatus: updateSubmissionStatus,
    updateSubmissionMetaData: updateSubmissionMetaData,
    updateSubmissionAnswer: updateSubmissionAnswer,
    createSubmissionTraining: createSubmissionTraining,
    updateSubmissionTraining: updateSubmissionTraining,
    createSubmissionBundle: createSubmissionBundle,
    updateSubmissionBundle: updateSubmissionBundle,
    createSubmissionRelation: createSubmissionRelation,
    createSubmissionLink: createSubmissionLink,
    deleteSubmissionLink: deleteSubmissionLink,
    deleteSubmissionAttachments: deleteSubmissionAttachments,
  };
};

export default usePowerSyncWriteData;
